import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Home } from './pages/Home';
import { Offer } from './pages/Offer';
import { Legal } from './pages/Legal';
import { Go } from './pages/Go';
import { SeoManager } from './seo/SeoManager';
import { Engagement } from './analytics/Engagement';
import { captureAttribution } from './offers/attribution';
import { getOffer } from './offers/config';
import { LanguageProvider } from './i18n/LanguageContext';
const METRICOOL_HASH = 'df4ba11b5f84ffc1f3c2fca17f7e75d4';

// ============================================================
// META PIXELS
// El PRIMERO es el master: acumula audiencias y aprendizaje de
// todo Blinkblis, para siempre. Ese no se quita nunca.
// Para alimentar otro dataset, agregalo a la lista.
// ============================================================
const META_PIXELS: string[] = ['553159443560590' // Blinkblis — MASTER
];

// Devuelve el slug de oferta si la ruta es una pagina de oferta,
// en cualquiera de los dos idiomas.
function offerSlugFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/(?:oferta|offer)\/([^/]+)\/?$/);
  return m ? m[1] : null;
}
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
function AttributionCapture() {
  const {
    search
  } = useLocation();
  useEffect(() => {
    captureAttribution();
  }, [search]);
  return null;
}
function RedirectKeepQuery({
  to
}: {
  to: string;
}) {
  const {
    search
  } = useLocation();
  return <Navigate to={`${to}${search}`} replace data-id="element-0" data-mp="38li8dgjgy7o4" />;
}

// ============================================================
// EVENTOS DE EMBUDO
// PageView       -> todas las rutas (la SPA no recarga, va manual)
// ViewContent    -> /oferta/* y /offer/*
// AffiliateClick -> click de salida al afiliado (pages/Go.tsx)
//
// NO usamos Lead para el click de salida. Un click hacia Kovo no es
// un lead de Blinkblis: no capturamos correo ni telefono en ese paso.
// Si algun dia se captura, ese si seria Lead y AffiliateClick queda
// como esta.
//
// Los de diagnostico (ScrollDepth, TimeOnPage, ViewFAQ,
// CheckedCredibility, SocialClick) viven en analytics/Engagement.tsx
// ============================================================
function PixelEvents() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const w = window as any;
    if (typeof w.fbq !== 'function' || !META_PIXELS.length) return;

    // La ruta de redireccion no cuenta como vista de pagina.
    if (pathname.startsWith('/go/')) return;
    META_PIXELS.forEach(id => w.fbq('trackSingle', id, 'PageView'));
    const slug = offerSlugFromPath(pathname);
    const offer = slug ? getOffer(slug) : undefined;
    if (offer) {
      const payload = {
        content_name: offer.brand,
        content_category: offer.category.en,
        content_ids: [offer.slug],
        content_type: 'product'
      };
      META_PIXELS.forEach(id => w.fbq('trackSingle', id, 'ViewContent', payload));
    }
  }, [pathname]);
  return null;
}
export function App() {
  useEffect(() => {
    if (document.getElementById('metricool-tracker')) return;
    const script = document.createElement('script');
    script.id = 'metricool-tracker';
    script.type = 'text/javascript';
    script.src = 'https://tracker.metricool.com/resources/be.js';
    const init = () => {
      const w = window as unknown as {
        beTracker?: {
          t: (opts: {
            hash: string;
          }) => void;
        };
      };
      if (w.beTracker) w.beTracker.t({
        hash: METRICOOL_HASH
      });
    };
    script.onload = init;
    (script as any).onreadystatechange = init;
    document.getElementsByTagName('head')[0].appendChild(script);
  }, []);
  useEffect(() => {
    if (!META_PIXELS.length) return;
    const w = window as any;
    if (w.fbq) return;
    const n: any = w.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!w._fbq) w._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.getElementsByTagName('head')[0].appendChild(t);
    META_PIXELS.forEach(id => w.fbq('init', id));
  }, []);
  return <LanguageProvider data-id="element-1" data-mp="c6eq2usn7x9h">
      <BrowserRouter data-id="element-2" data-mp="19vrlpph4z1k7">
        <ScrollToTop data-id="element-3" data-mp="19uyiuzlasw7c" />
        <AttributionCapture data-id="element-4" data-mp="19vhslt0soeaa" />
        <SeoManager data-id="element-5" data-mp="19tndivc7fge4" />
        <PixelEvents data-id="element-6" data-mp="14wewwz6pfkez" />
        <Engagement data-id="element-7" data-mp="14xyfiwfu27jx" />
        <Routes data-id="element-8" data-mp="14xgbhdmxip1g">
          <Route path="/" element={<Home data-id="element-10" data-mp="zxk2t13ae9fk" />} data-id="element-9" data-mp="14utzn1t09ndf" />

          {/* Oferta: una ruta por idioma, cada una canonica de si misma */}
          <Route path="/oferta/:slug" element={<Offer data-id="element-12" data-mp="vvgq694vsysp" />} data-id="element-11" data-mp="vwidbbz69bqs" />
          <Route path="/offer/:slug" element={<Offer data-id="element-14" data-mp="3knt0cx1gsnnc" />} data-id="element-13" data-mp="vvywxzp66vev" />

          {/* Salida al afiliado */}
          <Route path="/go/:slug" element={<Go data-id="element-16" data-mp="3gmh96n2xzud1" />} data-id="element-15" data-mp="3kpduiljva4ko" />

          {/* Atajos para bios de redes, conservan el query string */}
          <Route path="/credito" element={<RedirectKeepQuery to="/oferta/credito" data-id="element-18" data-mp="1w5q4cfjjm5ah" />} data-id="element-17" data-mp="3gotvtdjnaswf" />
          <Route path="/credit" element={<RedirectKeepQuery to="/offer/credit" data-id="element-20" data-mp="206a1hj4498wm" />} data-id="element-19" data-mp="205ihl5jvszm6" />

          <Route path="/:slug" element={<Legal data-id="element-22" data-mp="253sd5h9iq28b" />} data-id="element-21" data-mp="25424p9a7ohg4" />
          <Route path="*" element={<Navigate to="/" replace data-id="element-24" data-mp="29wd4mc4yc38o" />} data-id="element-23" data-mp="253a7xv4vodo9" />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>;
}